import React from "react";
import {
  ArrayField,
  Datagrid,
  NumberField,
  TextField,
  useRecordContext,
  FunctionField,
} from "react-admin";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { DateTimeField } from "lib/components/DateTime";
import { Achievement } from "types/graphql";
import { SVGFromUrl } from "lib/components/SVGFromUrl";
import { AddBadge } from "./AddBadge";
import { RemoveBadgeField } from "./RemoveBadgeField";
import { EditBadgeButton } from "./EditBadgeButton";

const useStyles = makeStyles(() => ({
  badgeImage: {
    width: "50px",
    height: "50px",
  },
}));

// Separate component for badge image to use hooks properly
interface BadgeImageProps {
  url: string;
  alt: string;
}

const BadgeImage: React.FC<BadgeImageProps> = ({ url, alt }) => {
  const classes = useStyles();
  const [imageType, setImageType] = React.useState<
    "unknown" | "raster" | "svg"
  >("unknown");

  React.useEffect(() => {
    // Check if URL has image extension
    const lowerUrl = url.toLowerCase();
    if (
      lowerUrl.includes(".jpg") ||
      lowerUrl.includes(".jpeg") ||
      lowerUrl.includes(".png")
    ) {
      setImageType("raster");
    } else if (lowerUrl.includes(".svg")) {
      setImageType("svg");
    } else {
      // Try to load as image to detect type
      const img = new Image();
      img.onload = () => setImageType("raster");
      img.onerror = () => setImageType("svg");
      img.src = url;
    }
  }, [url]);

  if (imageType === "unknown") {
    return <div className={classes.badgeImage}>Loading...</div>;
  }

  if (imageType === "raster") {
    return (
      <div className={classes.badgeImage}>
        <img
          src={url}
          alt={alt}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
    );
  }

  return (
    <div className={classes.badgeImage}>
      <SVGFromUrl url={url} />
    </div>
  );
};

interface BadgesProps {
  source?: string;
}

export function Badges(props: BadgesProps): JSX.Element {
  const achievementRecord = useRecordContext<Achievement>(props);

  return (
    <Box>
      <Box display="flex" alignItems="center" p={1}>
        <Typography variant="h6">Badges</Typography>
        <AddBadge achievementId={achievementRecord.id} />
      </Box>

      <ArrayField source="badges">
        <Datagrid>
          <TextField source="name" />
          <TextField source="description" />
          <NumberField source="amountToEarn" />
          <FunctionField
            label="Active Badge"
            render={(record) => {
              if (!record?.activeImageUrl) {
                return null;
              }
              return (
                <BadgeImage url={record.activeImageUrl} alt="active badge" />
              );
            }}
          />
          <FunctionField
            label="Inactive Badge"
            render={(record) => {
              if (!record?.inactiveImageUrl) {
                return null;
              }
              return (
                <BadgeImage
                  url={record.inactiveImageUrl}
                  alt="inactive badge"
                />
              );
            }}
          />
          <DateTimeField source="createdAt" label="Created At" />
          <DateTimeField source="updatedAt" label="Updated At" />
          <EditBadgeButton />
          <RemoveBadgeField />
        </Datagrid>
      </ArrayField>
    </Box>
  );
}
